import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { t } from "i18next";
import {
  BaseQueryApi,
  QueryReturnValue,
} from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { AdminState } from "../../../stores/AdminStore";
import { setAccessToken, signOut } from "../../../stores/AuthSlice";
import { msalInstance } from "../../../utils/authentication";
import { GetErrorMessage, isFetchArgs } from "../../../utils/apiUtils";
import toast from "react-hot-toast/headless";
import { WingmanAdminDocument } from "../models/WingmanAdminDocument";
import { brandsArrayForAdminDocumentsAPI } from "../utils/helpers";

interface CustomFetchArgs extends FetchArgs {
  hideToast?: boolean;
}
export interface UploadRequest {
  files: File[];
  roles: string[];
  brands: string[];
  viewables: boolean[];
}

export interface UpdateRolesRequest {
  file_name: string;
  roles: string;
}

export interface UpdateBrandsRequest {
  file_name: string;
  brands: string;
}

export interface UpdateViewableRequest {
  file_name: string;
  viewable: boolean;
}

export interface UpdateViewableResponse {
  success: boolean;
  message: string;
}

export interface UpdateRolesResponse {
  success: boolean;
  message: string;
}

export interface UpdateBrandsResponse {
  success: boolean;
  message: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  error_files?: { filename: string }[];
}

export interface DeleteRequest {
  Filename: string;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

export interface DocumentReplaceRequest {
  file_name: string;
  updated_file: File;
  roles: string[];
  brands: string[];
  viewables: boolean[];
}

export interface DocumentReplaceResponse {
  success: boolean;
  message: string;
}

export interface GetDocumentsByBrandParams {
  brand: string;
  authorizedBrands?: string[];
}

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_UNLEASHED_WINGMAN_ADMIN_API_URL}/`,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { accessToken },
    } = getState() as AdminState;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const refreshTokenQuery = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  // fetchBaseQuery requires {} as the param
  extraOptions: {}
) => {
  return async () => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const authResult = await msalInstance.acquireTokenSilent({
        scopes: [
          "openid",
          "offline_access",
          import.meta.env.VITE_AZURE_B2C_APP_ID,
        ],
      });

      if (authResult.accessToken) {
        api.dispatch(setAccessToken(authResult.accessToken));
        result = await baseQuery(args, api, extraOptions);
      } else {
        msalInstance.logoutRedirect().then(() => api.dispatch(signOut()));
      }
    }

    return result;
  };
};

const isPlainTextResponse = (
  queryResult: QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  >
) =>
  queryResult.meta?.response?.headers
    .get("content-type")
    ?.includes("text/plain");

const wingmanAdminBaseQuery: BaseQueryFn<
  string | CustomFetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args: string | CustomFetchArgs, api, extraOptions) => {
  const query = refreshTokenQuery(args, api, extraOptions);

  if (!isFetchArgs(args) || args.method === "GET") {
    const result = await query();

    if (result.error) {
      toast.error(GetErrorMessage(result.error, isPlainTextResponse(result)));
    }

    return result;
  } else {
    const promise = async () => {
      const result = await query();

      if (result.error) {
        throw GetErrorMessage(result.error, isPlainTextResponse(result));
      }

      return result;
    };

    if (args?.hideToast) {
      return promise();
    }

    // Check if the URL is `document_list_brand` and disable success toast if true to prevent multip toasts
    const isDocumentListBrand =
      typeof args === "object" &&
      (args.url === "/document_list_brand/" || args.url === "/document_check/");
    if (isDocumentListBrand) {
      return promise();
    }

    return toast.promise(promise(), {
      loading: args.method === "DELETE" ? `${t`Deleting`}...` : `${t`Saving`}...`,
      success: `${t`Success`}!`,
      error: (e) => e,
    });
  }
};

export const WingmanAdminAPI = createApi({
  reducerPath: "wingmanAdminApi",
  baseQuery: wingmanAdminBaseQuery,
  tagTypes: ["document_list_brand", "uploadfiles"],
  endpoints: (builder) => ({
    getSourceDocument: builder.query<
      { fileUrl: string; fileName: string },
      { file_name: string }
    >({
      query: ({ file_name }) => {
        const formData = new FormData();
        formData.append("file_name", file_name);

        return {
          url: "/document_download/",
          method: "POST",
          body: formData,
          headers: {
            redirect: "manual",
          },
          responseHandler: async (response) => {
            const arrayBuffer = await response.arrayBuffer();
            const fileBlob = new Blob([arrayBuffer], {
              type: "application/pdf",
            });
            const fileUrl = URL.createObjectURL(fileBlob);
            return { fileUrl, fileName: file_name };
          },
          hideToast: true,
        };
      },
    }),
    getWingmanAdminDocumentsByBrand: builder.query<
      WingmanAdminDocument[],
      GetDocumentsByBrandParams
    >({
      query: ({ brand, authorizedBrands }) => {
        const formData = new FormData();

        // For All Brands | UB get back all documents
        if (brand === "UB" && authorizedBrands?.length) {
          if (authorizedBrands.includes("UB")) {
            const brandsString = brandsArrayForAdminDocumentsAPI.join(",");
            formData.append("brand", brandsString);
          }
        } else {
          formData.append("brand", brand);
        }

        return {
          url: "/document_list_brand/",
          method: "POST",
          body: formData,
        };
      },
      providesTags: ["document_list_brand"],
    }),
    checkIfDocumentNameExists: builder.query<string[], string[]>({
      query: (filenames) => {
        const formData = new FormData();

        filenames.forEach((filename) => {
          formData.append("file_name", filename);
        });

        return {
          url: "/document_check/",
          method: "POST",
          body: formData,
        };
      },
    }),
    updateWingmanAdminDocumentRoles: builder.mutation<
      UpdateRolesResponse,
      UpdateRolesRequest
    >({
      query: (payload) => {
        const formData = new FormData();

        formData.append("file_name", payload.file_name);
        formData.append("roles", payload.roles);

        return {
          url: "/document_role_edit/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["document_list_brand"],
    }),
    updateWingmanAdminDocumentViewable: builder.mutation<
      UpdateViewableResponse,
      UpdateViewableRequest
    >({
      query: (payload) => {
        const formData = new FormData();

        formData.append("file_name", payload.file_name);
        formData.append("viewable", payload.viewable.toString());

        return {
          url: "/document_viewable_edit/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["document_list_brand"],
    }),
    updateWingmanAdminDocumentBrands: builder.mutation<
      UpdateBrandsResponse,
      UpdateBrandsRequest
    >({
      query: (payload) => {
        const formData = new FormData();

        formData.append("file_name", payload.file_name);
        formData.append("brands", payload.brands);

        return {
          url: "/document_brand_edit/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["document_list_brand"],
    }),
    uploadWingmanAdminDocuments: builder.mutation<
      UploadResponse,
      UploadRequest
    >({
      query: (payload) => {
        const formData = new FormData();

        payload.files.forEach((file) => {
          formData.append("files", file);
        });

        payload.brands.forEach((brand) => {
          formData.append("brands", brand);
        });

        payload.roles.forEach((role) => {
          formData.append("roles", role);
        });

        payload.viewables.forEach((viewable) => {
          formData.append("viewables", viewable.toString());
        });

        return {
          url: "/uploadfiles/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["document_list_brand"],
    }),
    deleteWingmanAdminDocument: builder.mutation<DeleteResponse, DeleteRequest>(
      {
        query: (payload) => {
          const formData = new FormData();
          formData.append("file_name", payload.Filename);

          return {
            url: "/document_delete/",
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: ["document_list_brand"],
      }
    ),
    documentReplace: builder.mutation<
      DocumentReplaceResponse,
      DocumentReplaceRequest
    >({
      query: (payload) => {
        const formData = new FormData();

        formData.append("file_name", payload.file_name);
        formData.append("updated_file", payload.updated_file);

        formData.append("roles", `${payload.roles.join(", ")}`);
        formData.append("brands", `${payload.brands.join(", ")}`);

        payload.viewables.forEach((viewable) => {
          formData.append("viewables", viewable.toString());
        });

        return {
          url: "/document_replace/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["document_list_brand"],
    }),
  }),
});

export const {
  useGetWingmanAdminDocumentsByBrandQuery,
  useLazyCheckIfDocumentNameExistsQuery,
  useUploadWingmanAdminDocumentsMutation,
  useUpdateWingmanAdminDocumentBrandsMutation,
  useUpdateWingmanAdminDocumentViewableMutation,
  useUpdateWingmanAdminDocumentRolesMutation,
  useDeleteWingmanAdminDocumentMutation,
  useDocumentReplaceMutation,
  useGetSourceDocumentQuery,
} = WingmanAdminAPI;

